import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { useCartContext, useWarehouseContext, useAuthContext, } from "../../../app/providers/contextIndex";
import AppToast from "../../../shared/components/AppToast";
import { abrirCotizacionPDF } from "../../../services/backend/cotizacionService";
import { getAffariClienteCodigo } from "../../../services/backend/authService";
import { actualizarMarketing, getAceptaMarketing, } from "../../../services/backend/usuarioService";
import CheckoutBuyerSection from "../components/CheckoutBuyerSection";
import CheckoutDeliverySection from "../components/CheckoutDeliverySection";
import CheckoutFlowStepper, { CHECKOUT_FLOW_STEPS, STEP_STATE, } from "../components/CheckoutFlowStepper";
import CheckoutPaymentSection from "../components/CheckoutPaymentSection";
import CheckoutStatusCard from "../components/CheckoutStatusCard";
import CheckoutSummaryCard from "../components/CheckoutSummaryCard";
import useBuyerForm from "../hooks/useBuyerForm";
import useDelivery from "../hooks/useDelivery";
import useCheckoutSummary from "../hooks/useCheckoutSummary";
import useCheckoutDetail from "../hooks/useCheckoutDetail";
import usePayment from "../hooks/usePayment";
import useCoupon from "../hooks/useCoupon";
import { CLP_FORMATTER, parseFormattedAmount } from "../../../utils/currency";
import { getCompany } from "../../../config/companyConfig";
import { StorageKeys, session } from "../../../utils/storage";
import useBuyerContactAssociation from "../hooks/useBuyerContactAssociation";
import useSelectedSucursalInfo from "../hooks/useSelectedSucursalInfo";

const CheckoutFlow = ({ step = "checkout" }) => {
  const navigate = useNavigate();
  const { cotizacionId } = useCartContext();
  const { almacenes, selectedAlmacen } = useWarehouseContext();
  const { appCanal } = useAuthContext();
  const toastRef = useRef(null);

  const [clienteCodigo] = useState(() =>
    String(getAffariClienteCodigo() || "").trim()
  );
  const userId = session.get(StorageKeys.AUTH_ID);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const buyer = useBuyerForm({ clienteCodigo });
  const summary = useCheckoutSummary({ cotizacionId });
  const detail = useCheckoutDetail({ cotizacionId });
  const refreshSummaryAndDetail = useCallback(async (id) => {
    await Promise.all([
      summary.refreshCheckoutSummary(id),
      detail.refreshCheckoutDetail(id),
    ]);
  }, [summary, detail]);
  const coupon = useCoupon({cotizacionId, onRefreshCheckoutSummary: refreshSummaryAndDetail,});
  const delivery = useDelivery({
    cotizacionId,
    buyerClientInfo: buyer.buyerClientInfo,
    onShowToast: (config) => toastRef.current?.show(config),
    onRefreshCheckoutSummary: summary.refreshCheckoutSummary,
  });

  const selectedSucursalInfo = useSelectedSucursalInfo({
    cotizacionId,
    selectedAlmacen,
    almacenes,
  });

  const sucursal = selectedSucursalInfo.sucursal || "";
  const almacen = selectedSucursalInfo.almacen || "";

  const totalItems = Number(summary.checkoutResumen?.lineas) || 0;
  const currentStepIndex = STEP_STATE[step] ?? 0;
  const currencyFormatter = CLP_FORMATTER;
  const displaySubtotal = summary.checkoutResumen?.subtotal;
  const displayIva = summary.checkoutResumen?.iva;
  const displayShipping =
    typeof summary.checkoutResumen?.flete === "number"
      ? currencyFormatter.format(summary.checkoutResumen.flete)
      : summary.checkoutResumen?.flete;
  const displayTotal = summary.checkoutResumen?.total || null;
  const paymentTotal =
    parseFormattedAmount(summary.checkoutResumen?.total) || null;

  const showToast = useCallback((config) => toastRef.current?.show(config), []);

  const handleMarketingChange = useCallback(
    async (checked) => {
      if (!userId) {
        showToast({
          severity: "warn",
          summary: "Preferencias de marketing",
          detail:
            "No se pudo identificar tu usuario. Inicia sesión nuevamente.",
          life: 3500,
        });
        return;
      }
      setMarketingAccepted(checked);
      try {
        await actualizarMarketing(userId, checked);
        showToast({
          severity: "success",
          summary: "Preferencias de marketing",
          detail: checked
            ? "Aceptaste recibir información y promociones."
            : "Dejaste de aceptar marketing.",
          life: 3000,
        });
      } catch (error) {
        setMarketingAccepted(!checked);
        showToast({
          severity: "error",
          summary: "Preferencias de marketing",
          detail:
            error?.message ||
            "No fue posible actualizar tu preferencia de marketing.",
          life: 3500,
        });
      }
    },
    [userId, showToast]
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    getAceptaMarketing(userId)
      .then((response) => {
        if (cancelled) return;
        setMarketingAccepted(String(response).trim().toLowerCase() === "true");
      })
      .catch(() => {
        if (cancelled) return;
        setMarketingAccepted(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const ensureBuyerContactAssociation = useBuyerContactAssociation({
    clienteCodigo,
    cotizacionId,
    buyer,
  });

  const payment = usePayment({
    total: paymentTotal,
    cotizacionId,
    onValidateBuyer: async (detail) => {
      if (!termsAccepted) {
        showToast({
          severity: "warn",
          summary: "Términos y condiciones",
          detail: "Debes aceptar los términos y condiciones para continuar.",
          life: 3500,
        });
        return false;
      }
      if (!buyer.validateBuyerBeforeContinue(detail, showToast)) return false;
      if (paymentTotal <= 0) {
        showToast({
          severity: "warn",
          summary: "Monto inválido",
          detail: "El monto a pagar debe ser mayor a 0.",
          life: 3500,
        });
        return false;
      }
      await ensureBuyerContactAssociation();
      return true;
    },
    onShowToast: showToast,
  });

  const handleDownloadCotizacion = () => {
    if (!cotizacionId) {
      showToast({
        severity: "warn",
        summary: "Cotización no disponible",
        detail: "Sin cotización activa.",
        life: 3500,
      });
      return;
    }
    abrirCotizacionPDF(cotizacionId);
  };

  const renderCheckout = () => (
    <div className="getnet-flow-grid">
      <Card className="getnet-main-card">
        <div className="getnet-stack">
          <CheckoutBuyerSection
            buyerClientLoading={buyer.buyerClientLoading}
            buyerClientError={buyer.buyerClientError}
            buyerClientInfo={buyer.buyerClientInfo}
            buyerContactsLoading={buyer.buyerContactsLoading}
            buyerContactsError={buyer.buyerContactsError}
            buyerContactOptions={buyer.buyerContactOptions}
            selectedBuyerContactId={buyer.selectedBuyerContactId}
            onBuyerContactChange={buyer.handleBuyerContactChange}
            buyerFields={buyer.buyerFields}
            buyerForm={buyer.buyerForm}
            buyerTouched={buyer.buyerTouched}
            buyerErrors={buyer.buyerErrors}
            onBuyerFieldChange={buyer.handleBuyerFieldChange}
            onBuyerFieldBlur={buyer.handleBuyerFieldBlur}
            termsAccepted={termsAccepted}
            onTermsChange={setTermsAccepted}
            marketingAccepted={marketingAccepted}
            onMarketingChange={handleMarketingChange}
          />
          <CheckoutDeliverySection
            delivery={delivery}
            selectedSucursal={sucursal}
            selectedAlmacen={almacen}
          />
        </div>
        <div className="getnet-stack">
          <CheckoutPaymentSection
            onPayWithGetnet={payment.handleTransactionRequest}
            onPayWithCredito={payment.handleCreditoRequest}
            paymentLoading={payment.paymentLoading}
            isPaymentBusy={payment.isPaymentBusy}
            isPaymentReady={payment.isPaymentReady}
            appCanal={appCanal}
          />
        </div>
      </Card>
      <CheckoutSummaryCard
        totalItems={totalItems}
        displaySubtotal={displaySubtotal}
        displayIva={displayIva}
        displayShipping={displayShipping}
        displayTotal={displayTotal}
        onDownloadCotizacion={handleDownloadCotizacion}
        onOpenSpecialistWhatsApp={() => window.open(getCompany().whatsappUrl, "_blank", "noopener,noreferrer")}
        coupon={coupon}
        detail={detail.checkoutDetail}
      />
    </div>
  );

  const renderSuccess = () => (
    <CheckoutStatusCard
      variant="is-success"
      chipClassName="is-success"
      chipText="Pago aprobado"
      title="Pago realizado correctamente"
      description="La compra quedó confirmada."
      iconClassName="pi pi-check"
      metaRows={[
        { label: "Cotización", value: cotizacionId },
        { label: "Monto pagado", value: paymentTotal },
      ]}
      primaryAction={{
        label: "Ver mi cuenta",
        onClick: () => navigate("/mi-cuenta"),
      }}
      secondaryAction={{
        label: "Volver al inicio",
        onClick: () => navigate("/"),
      }}
    />
  );

  const renderRejected = () => (
    <CheckoutStatusCard
      variant="is-error"
      chipClassName="is-error"
      chipText="Pago rechazado"
      title="No fue posible completar el pago"
      description="Puedes intentar nuevamente o volver al checkout."
      iconClassName="pi pi-times"
      metaRows={[
        { label: "Motivo", value: "Autorización rechazada" },
        { label: "Cotización", value: cotizacionId },
        { label: "Monto", value: paymentTotal },
      ]}
      primaryAction={{
        label: "Intentar nuevamente",
        onClick: () => navigate("/checkout"),
      }}
      secondaryAction={{
        label: "Volver al checkout",
        onClick: () => navigate("/checkout"),
      }}
    />
  );

  const renderStepContent = () => {
    switch (step) {
      case "success":
        return renderSuccess();
      case "rejected":
        return renderRejected();
      default:
        return renderCheckout();
    }
  };

  return (
    <div className="landing-container getnet-page">
      <AppToast ref={toastRef} />
      <CheckoutFlowStepper
        steps={CHECKOUT_FLOW_STEPS}
        currentStepIndex={currentStepIndex}
      />
      {renderStepContent()}
    </div>
  );
};

export default CheckoutFlow;
